import React from "react";
import { Box, Typography } from "@mui/material";
import {
  BathtubOutlined,
  BedOutlined,
  GrassOutlined,
  PaidOutlined,
  SquareFootOutlined,
} from "@mui/icons-material";
import { DASH, cardSx } from "../../dashboard/tokens";

type Overview = {
  estimatedValue?: number | string | null;
  bedrooms?: number | string | null;
  bathrooms?: number | string | null;
  sqft?: number | string | null;
  lotSize?: number | string | null;
  lotAcres?: number | string | null;
};

const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const fmt = (n: number) => n.toLocaleString("en-US");

/** The five public numbers — shown to every viewer, free included (Task 3/10). */
export const buildStats = (o: Overview = {}) => {
  const value = num(o.estimatedValue);
  const beds = num(o.bedrooms);
  const baths = num(o.bathrooms);
  const sqft = num(o.sqft);
  const lot = num(o.lotSize);
  const acres = num(o.lotAcres);
  return [
    { key: "value", label: "Estimated Value", value: value ? `$${fmt(value)}` : null, icon: <PaidOutlined /> },
    { key: "beds", label: "Beds", value: beds ? fmt(beds) : null, icon: <BedOutlined /> },
    { key: "baths", label: "Baths", value: baths ? fmt(baths) : null, icon: <BathtubOutlined /> },
    { key: "sqft", label: "Finished Sq Ft", value: sqft ? fmt(sqft) : null, icon: <SquareFootOutlined /> },
    {
      key: "lot",
      label: "Lot Size",
      value: lot ? `${fmt(lot)} sq ft` : acres ? `${acres} acres` : null,
      sub: lot && acres ? `${acres} acres` : undefined,
      icon: <GrassOutlined />,
    },
  ];
};

const StatsRow: React.FC<{ overview?: Overview }> = ({ overview }) => {
  const stats = buildStats(overview);
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
        gap: 3,
      }}
      data-testid="stats-row"
    >
      {stats.map((s, i) => (
        <Box
          key={s.key}
          sx={{
            ...cardSx,
            p: 4,
            display: "flex",
            // Icon above the numbers: five tiles share a row on desktop, and a
            // side-by-side icon squeezed "Estimated Value" onto two lines.
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
            minWidth: 0,
            // The odd fifth tile spans the row on phones instead of sitting alone.
            gridColumn: { xs: i === 0 ? "1 / -1" : "auto", sm: "auto" },
          }}
          data-testid={`stat-${s.key}`}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: "12px",
              bgcolor: DASH.peach,
              color: DASH.teal,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {s.icon}
          </Box>
          <Box sx={{ minWidth: 0, width: "100%" }}>
            <Typography component="dt" noWrap sx={{ fontSize: 12, fontWeight: 700, color: DASH.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {s.label}
            </Typography>
            <Typography component="dd" sx={{ m: 0, fontSize: 19, fontWeight: 800, color: "#1d2b2b", lineHeight: 1.25 }} noWrap>
              {s.value ?? <Box component="span" sx={{ color: DASH.muted }} aria-label="Not available">—</Box>}
            </Typography>
            {s.sub && (
              <Typography sx={{ fontSize: 12, color: DASH.muted }} noWrap>
                {s.sub}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default StatsRow;
